import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { data } from "../data/loadFactors";
import {
  calculate,
  defaultPositions,
  scenarioPositions,
  type CalculationResult,
} from "../lib/calc";
import type { Scenario } from "../data/schema";

/** The three foundational inputs whose product seeds the chain. */
export interface Foundation {
  galaxies: number;
  starsPerGalaxy: number;
  planetsPerStar: number;
}

/**
 * APP STATE
 * =========
 * The only mutable state is `positions`: a map of factor.id -> slider position
 * (0..1). Everything else — the per-factor values, the running products, the
 * final number — is *derived* from it via `calculate()`. This keeps the data
 * flow simple and predictable, which is exactly what you want when directing
 * changes through an AI assistant.
 *
 * Positions persist to localStorage so a refresh (or a re-open in a native
 * wrapper) keeps the user's exploration intact.
 */

const STORAGE_KEY = "factor-funnel:positions:v1";
const FOUNDATION_KEY = "factor-funnel:foundation:v1";

/** Sensible defaults if the dataset omits `foundation`. ~10^24 planets. */
const FALLBACK_FOUNDATION: Foundation = {
  galaxies: 2e12,
  starsPerGalaxy: 5e10,
  planetsPerStar: 10,
};

function defaultFoundation(): Foundation {
  return data.meta.foundation ?? FALLBACK_FOUNDATION;
}

/** Product of the three foundational inputs: the starting planet count. */
export function foundationToStart(f: Foundation): number {
  return f.galaxies * f.starsPerGalaxy * f.planetsPerStar;
}

interface FactorsContextValue {
  /** Static, validated dataset (meta, categories, factors). */
  data: typeof data;
  /** Current slider positions, keyed by factor id. */
  positions: Record<string, number>;
  /** The editable foundational inputs (galaxies/stars/planets). */
  foundation: Foundation;
  /** Derived calculation (start, per-factor steps, final total). */
  result: CalculationResult;
  /** Move a single slider. */
  setPosition: (factorId: string, t: number) => void;
  /** Update one foundational input. */
  setFoundation: (key: keyof Foundation, value: number) => void;
  /** Snap every slider to a named scenario. */
  applyScenario: (scenario: Scenario) => void;
  /** Reset sliders back to consensus. */
  reset: () => void;
  /** Reset the foundational inputs to their dataset defaults. */
  resetFoundation: () => void;
  /** Which named scenario is currently active, or null if custom. */
  activeScenario: Scenario | null;
}

const FactorsContext = createContext<FactorsContextValue | null>(null);

function loadStoredPositions(): Record<string, number> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return defaultPositions(data.factors);
    const parsed = JSON.parse(stored) as Record<string, number>;
    // Merge over defaults so newly added factors get a sensible position.
    return { ...defaultPositions(data.factors), ...parsed };
  } catch {
    return defaultPositions(data.factors);
  }
}

/** Detect whether all positions match one named scenario exactly. */
function detectScenario(
  positions: Record<string, number>,
): Scenario | null {
  const scenarios: Scenario[] = ["pessimistic", "consensus", "optimistic"];
  for (const s of scenarios) {
    const target = scenarioPositions(data.factors, s);
    const matches = data.factors.every(
      (f) => Math.abs((positions[f.id] ?? 0.5) - target[f.id]) < 1e-9,
    );
    if (matches) return s;
  }
  return null;
}

function loadStoredFoundation(): Foundation {
  try {
    const stored = localStorage.getItem(FOUNDATION_KEY);
    if (!stored) return defaultFoundation();
    const parsed = JSON.parse(stored) as Partial<Foundation>;
    return { ...defaultFoundation(), ...parsed };
  } catch {
    return defaultFoundation();
  }
}

export function FactorsProvider({ children }: { children: ReactNode }) {
  const [positions, setPositions] = useState<Record<string, number>>(
    loadStoredPositions,
  );
  const [foundation, setFoundationState] =
    useState<Foundation>(loadStoredFoundation);

  // Persist on every change.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
    } catch {
      /* storage unavailable (e.g. private mode) — non-fatal */
    }
  }, [positions]);

  useEffect(() => {
    try {
      localStorage.setItem(FOUNDATION_KEY, JSON.stringify(foundation));
    } catch {
      /* non-fatal */
    }
  }, [foundation]);

  const setPosition = useCallback((factorId: string, t: number) => {
    setPositions((prev) => ({ ...prev, [factorId]: t }));
  }, []);

  const setFoundation = useCallback(
    (key: keyof Foundation, val: number) => {
      setFoundationState((prev) => ({ ...prev, [key]: val }));
    },
    [],
  );

  const applyScenario = useCallback((scenario: Scenario) => {
    setPositions(scenarioPositions(data.factors, scenario));
  }, []);

  const reset = useCallback(() => {
    setPositions(defaultPositions(data.factors));
  }, []);

  const resetFoundation = useCallback(() => {
    setFoundationState(defaultFoundation());
  }, []);

  const result = useMemo(
    () => calculate(data.factors, positions, foundationToStart(foundation)),
    [positions, foundation],
  );

  const activeScenario = useMemo(() => detectScenario(positions), [positions]);

  const value: FactorsContextValue = {
    data,
    positions,
    foundation,
    result,
    setPosition,
    setFoundation,
    applyScenario,
    reset,
    resetFoundation,
    activeScenario,
  };

  return (
    <FactorsContext.Provider value={value}>{children}</FactorsContext.Provider>
  );
}

/** Access app state from any component. */
export function useFactors(): FactorsContextValue {
  const ctx = useContext(FactorsContext);
  if (!ctx) throw new Error("useFactors must be used within <FactorsProvider>");
  return ctx;
}
